/**
 * Dashboard Widgets — shared small components
 * TabButton, StatsStrip, FilterChip, FormField, UserSelectField, TagInput, ActionButton, useCountUp
 */

import { useState, useEffect, useRef, useMemo } from "react";
import {
  LayoutTemplate,
  Activity,
  Crown,
  TrendingUp,
  X,
  User,
  ChevronDown,
} from "lucide-react";
import { getTagColor, ROLE_LABELS, ROLE_BADGE_COLORS } from "./dashboardConstants";
import type { UserRecord, UserRole } from "../../utils/userApi";

/* ================================================================
   Action Button (icon button in card footer)
   ================================================================ */

export function ActionButton({
  onClick,
  onKeyDown,
  title,
  disabled,
  hoverClass,
  children,
}: {
  onClick: (e: React.MouseEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  title: string;
  disabled?: boolean;
  hoverClass: string;
  children: React.ReactNode;
}) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={disabled ? undefined : onKeyDown}
      className={`p-2 text-gray-400 rounded-lg transition-all cursor-pointer ${hoverClass} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
      title={title}
    >
      {children}
    </span>
  );
}

/* ================================================================
   Tab Button
   ================================================================ */

export function TabButton({ active, onClick, icon, label, count }: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count?: number;
}) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs cursor-pointer transition-all border-b-2 ${
        active
          ? "text-white border-zap-glow"
          : "text-white/50 border-transparent hover:text-white/80 hover:border-white/20"
      }`}
      style={{ fontWeight: active ? 600 : 400 }}
    >
      {icon}
      {label}
      {count !== undefined && (
        <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-white/10"}`}>
          {count}
        </span>
      )}
    </span>
  );
}

/* ================================================================
   Filter Chip (for toolbar)
   ================================================================ */

export function FilterChip({ active, onClick, label, count, icon }: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  icon?: React.ReactNode;
}) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs cursor-pointer transition-all ${
        active
          ? "bg-zap text-white shadow-sm"
          : "text-gray-500 hover:bg-gray-50"
      }`}
      style={{ fontWeight: active ? 600 : 400 }}
    >
      {icon}
      {label}
      {count !== undefined && (
        <span className={`px-1.5 py-0.5 rounded text-[10px] ${
          active ? "bg-white/25" : "bg-gray-100 text-gray-400"
        }`} style={{ fontWeight: 600 }}>
          {count}
        </span>
      )}
    </span>
  );
}

/* ================================================================
   Form Field
   ================================================================ */

export function FormField({
  label,
  value,
  onChange,
  placeholder,
  required,
  dir = "rtl",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  dir?: "rtl" | "ltr";
}) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1.5 block" style={{ fontWeight: 500 }}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-zap/30 focus:border-zap-light outline-none bg-white transition-all"
        dir={dir}
        autoComplete="off"
      />
    </div>
  );
}

/* ================================================================
   User Select Field — dropdown to pick a user by role
   ================================================================ */

export function UserSelectField({
  label,
  value,
  onChange,
  users,
  placeholder,
}: {
  label: string;
  /** Currently selected username (empty = none) */
  value: string;
  onChange: (username: string) => void;
  /** Users to choose from (already filtered by role) */
  users: UserRecord[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q),
    );
  }, [users, search]);

  const selectedUser = users.find((u) => u.username === value);

  return (
    <div ref={containerRef} className="relative">
      <label className="text-xs text-gray-500 mb-1.5 block" style={{ fontWeight: 500 }}>
        {label}
      </label>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen(!open); }}
        className={`w-full flex items-center gap-2 px-3 py-2.5 border rounded-xl text-sm cursor-pointer bg-white transition-all ${
          open ? "ring-2 ring-zap/30 border-zap-light" : "border-gray-200 hover:border-gray-300"
        }`}
        dir="rtl"
      >
        {selectedUser ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center shrink-0">
              <User className="w-3 h-3 text-gray-500" />
            </div>
            <span className="truncate text-gray-800" style={{ fontWeight: 500 }}>
              {selectedUser.displayName}
            </span>
          </div>
        ) : (
          <span className="flex-1 text-gray-400 text-right">
            {placeholder || "\u05D1\u05D7\u05E8 \u05DE\u05E9\u05EA\u05DE\u05E9..."}
          </span>
        )}
        <div className="flex items-center gap-1 shrink-0">
          {selectedUser && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onChange(""); } }}
              className="p-0.5 text-gray-300 hover:text-red-400 rounded"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 ${open ? "rotate-180" : ""}`} style={{ transition: "transform 0.15s" }} />
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-30 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-hidden" dir="rtl">
          {/* Search within dropdown */}
          {users.length > 4 && (
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={"\u05D7\u05D9\u05E4\u05D5\u05E9..."}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-zap/30 bg-gray-50"
                dir="rtl"
                autoFocus
              />
            </div>
          )}
          <div className="max-h-36 overflow-y-auto">
            {/* Empty option */}
            <div
              role="button"
              tabIndex={0}
              onMouseDown={(e) => { e.preventDefault(); onChange(""); setOpen(false); setSearch(""); }}
              className={`flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 cursor-pointer ${
                !value ? "bg-zap-pale/30" : ""
              }`}
            >
              <span className="text-gray-400">{"\u05DC\u05DC\u05D0 \u05D4\u05E7\u05E6\u05D0\u05D4"}</span>
            </div>
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-gray-400 text-center">
                {users.length === 0 ? "\u05D0\u05D9\u05DF \u05DE\u05E9\u05EA\u05DE\u05E9\u05D9\u05DD \u05D1\u05EA\u05E4\u05E7\u05D9\u05D3 \u05D6\u05D4" : "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05EA\u05D5\u05E6\u05D0\u05D5\u05EA"}
              </div>
            ) : (
              filtered.map((u) => (
                <div
                  key={u.username}
                  role="button"
                  tabIndex={0}
                  onMouseDown={(e) => { e.preventDefault(); onChange(u.username); setOpen(false); setSearch(""); }}
                  className={`flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 cursor-pointer ${
                    value === u.username ? "bg-zap-pale/30" : ""
                  }`}
                >
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center shrink-0">
                    <User className="w-3 h-3 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-800 truncate block" style={{ fontWeight: 500 }}>
                      {u.displayName}
                    </span>
                    <span className="text-gray-400 text-[10px]" dir="ltr">@{u.username}</span>
                  </div>
                  {value === u.username && (
                    <span className="text-zap text-[10px]" style={{ fontWeight: 700 }}>&#10003;</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================
   Tag Input (with autocomplete)
   ================================================================ */

export function TagInput({
  tags,
  onChange,
  allTags,
  pendingRef,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
  allTags: string[];
  /** Optional ref the parent can read to get uncommitted input text */
  pendingRef?: { current: string };
}) {
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keep pendingRef in sync so parent can flush on submit
  useEffect(() => {
    if (pendingRef) pendingRef.current = input;
  }, [input, pendingRef]);

  const suggestions = useMemo(() => {
    if (!input.trim()) return allTags.filter((t) => !tags.includes(t));
    const q = input.toLowerCase();
    return allTags.filter((t) => !tags.includes(t) && t.toLowerCase().includes(q));
  }, [input, allTags, tags]);

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    onChange([...tags, trimmed]);
    setInput("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeTag = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  return (
    <div>
      <label className="text-xs text-gray-500 mb-1.5 block" style={{ fontWeight: 500 }}>
        {"\u05EA\u05D2\u05D9\u05D5\u05EA"}
      </label>
      {/* Existing tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {tags.map((tag) => {
            const tc = getTagColor(tag);
            return (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border"
                style={{ fontWeight: 500, backgroundColor: tc.bg, color: tc.text, borderColor: tc.border }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tc.dot }} />
                {tag}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeTag(tag)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") removeTag(tag); }}
                  className="mr-0.5 cursor-pointer hover:opacity-60"
                >
                  <X className="w-3 h-3" />
                </span>
              </span>
            );
          })}
        </div>
      )}
      {/* Input with suggestions */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => {
            // Auto-commit typed text as a tag when focus leaves the input
            if (input.trim()) addTag(input.trim());
            setTimeout(() => setShowSuggestions(false), 200);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (suggestions.length > 0 && input.trim()) {
                const exact = suggestions.find((s) => s.toLowerCase() === input.toLowerCase());
                addTag(exact || input.trim());
              } else if (input.trim()) {
                addTag(input.trim());
              }
            }
            if (e.key === "Backspace" && !input && tags.length > 0) {
              removeTag(tags[tags.length - 1]);
            }
          }}
          placeholder={"\u05EA\u05D2\u05D9\u05EA \u05D7\u05D3\u05E9\u05D4..."}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-zap/30 focus:border-zap-light outline-none bg-white transition-all"
          dir="rtl"
        />
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-20 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-32 overflow-y-auto">
            {suggestions.slice(0, 8).map((tag) => {
              const tc = getTagColor(tag);
              return (
                <span
                  key={tag}
                  role="button"
                  tabIndex={0}
                  onMouseDown={(e) => { e.preventDefault(); addTag(tag); }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tc.dot }} />
                  {tag}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ================================================================
   Count-Up Hook
   ================================================================ */

export function useCountUp(target: number, duration = 800): number {
  const [current, setCurrent] = useState(0);
  const prevTarget = useRef(0);

  useEffect(() => {
    if (target === prevTarget.current) return;
    const start = prevTarget.current;
    prevTarget.current = target;
    const startTime = performance.now();
    let rafId: number;

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(start + (target - start) * eased));
      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      }
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);

  return current;
}

/* ================================================================
   Stats Strip (compact toolbar-integrated)
   ================================================================ */

export function StatsStrip({ stats }: {
  stats: {
    total: number;
    brandingCount: number;
    storeCount: number;
    recentlyUpdated: number;
    masterBased: number;
  };
}) {
  const totalAnimated = useCountUp(stats.total);
  const recentAnimated = useCountUp(stats.recentlyUpdated);
  const masterAnimated = useCountUp(stats.masterBased);
  const brandingAnimated = useCountUp(stats.brandingCount);
  const storeAnimated = useCountUp(stats.storeCount);

  const items: {
    key: string;
    icon: React.ReactNode;
    color: string;
    value: React.ReactNode;
    label: string;
  }[] = [
    {
      key: "total",
      icon: <LayoutTemplate className="w-3.5 h-3.5" />,
      color: "text-zap bg-zap-pale",
      value: <span className="text-sm text-gray-800" style={{ fontWeight: 700 }}>{totalAnimated}</span>,
      label: "\u05E1\u05D4\u05F4\u05DB \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA",
    },
    {
      key: "recent",
      icon: <Activity className="w-3.5 h-3.5" />,
      color: "text-emerald-500 bg-emerald-50",
      value: <span className="text-sm text-gray-800" style={{ fontWeight: 700 }}>{recentAnimated}</span>,
      label: "\u05E2\u05D5\u05D3\u05DB\u05E0\u05D5 \u05D1-7 \u05D9\u05DE\u05D9\u05DD",
    },
    {
      key: "master",
      icon: <Crown className="w-3.5 h-3.5" />,
      color: "text-amber-500 bg-amber-50",
      value: <span className="text-sm text-gray-800" style={{ fontWeight: 700 }}>{masterAnimated}</span>,
      label: "\u05DE\u05D1\u05D5\u05E1\u05E1\u05D5\u05EA \u05DE\u05D0\u05E1\u05D8\u05E8",
    },
    {
      key: "status",
      icon: <TrendingUp className="w-3.5 h-3.5" />,
      color: "text-violet-500 bg-violet-50",
      value: (
        <span className="flex items-center gap-1">
          <span className="text-sm text-gray-800" style={{ fontWeight: 700 }}>{brandingAnimated}</span>
          <span className="text-[10px] text-zap-light" style={{ fontWeight: 500 }}>{"\u05EA\u05D3\u05DE\u05D9\u05EA"}</span>
          <span className="text-gray-300 text-xs mx-0.5">/</span>
          <span className="text-sm text-gray-800" style={{ fontWeight: 700 }}>{storeAnimated}</span>
          <span className="text-[10px] text-emerald-400" style={{ fontWeight: 500 }}>{"\u05E1\u05D7\u05E8"}</span>
        </span>
      ),
      label: "\u05DC\u05E4\u05D9 \u05E1\u05D8\u05D8\u05D5\u05E1",
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200/60 shadow-sm" dir="rtl">
      <span className="text-[11px] text-gray-400 ml-1" style={{ fontWeight: 500 }}>{"\u05E1\u05D8\u05D8\u05D9\u05E1\u05D8\u05D9\u05E7\u05D5\u05EA"}</span>
      {items.map((item, idx) => (
        <span key={item.key} className="contents">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-50/80 hover:bg-gray-100/80" style={{ transition: "background 0.2s" }}>
            <span className={`w-6 h-6 rounded-md flex items-center justify-center ${item.color}`}>
              {item.icon}
            </span>
            {item.value}
            <span className="text-[10px] text-gray-400" style={{ fontWeight: 400 }}>{item.label}</span>
          </span>
          {idx < items.length - 1 && (
            <span className="w-px h-5 bg-gray-200/80 mx-0.5 hidden sm:block" />
          )}
        </span>
      ))}
    </div>
  );
}